import { Consumable, Menu } from './Consumable'
import { Voucher } from './Voucher'
import { ModifierGroup } from '@app/domain/Modifier'

export class ProductLibrary {
    constructor(
        public id: string,
        public venueId: string,
        public consumables: Consumable[],
        public menus: Menu[],
        public vouchers: Voucher[],
        public modifierGroups: ModifierGroup[]
    ) { }

    addConsumable(consumable: Consumable): boolean {
        if (this.getConsumable(consumable.id)) {
            return false
        }
        this.consumables.push(consumable)
        return true
    }

    updateConsumable(consumable: Consumable): boolean {
        const index = this.consumables.findIndex(c => c.id === consumable.id)
        if (index === -1) {
            return false
        }
        this.consumables[index] = consumable
        return true
    }

    addVoucher(voucher: Voucher): boolean {
        if (this.getVoucher(voucher.id)) {
            return false
        }
        this.vouchers.push(voucher)
        return true
    }

    addMenu(menu: Menu): boolean {
        if (this.getMenu(menu.id)) {
            return false
        }
        this.menus.push(menu)
        return true
    }

    updateMenu(menu: Menu): boolean {
        const index = this.menus.findIndex(m => m.id === menu.id)
        if (index === -1) {
            return false
        }
        this.menus[index] = menu
        return true
    }

    getFirstOnSaleVoucher(): Voucher | null {
        return this.vouchers.find((voucher) => {
            return voucher.hasSKUsOnSale()
        }) || null
    }

    updateVoucher(voucher: Voucher): boolean {
        const index = this.vouchers.findIndex(v => v.id === voucher.id)
        if (index === -1) {
            return false
        }
        this.vouchers[index] = voucher
        return true
    }

    getConsumable(id: string): Consumable | null {
        return this.consumableWithId(id)
    }

    getMenu(id: string): Menu | null {
        return this.menuWithId(id)
    }

    getVoucher(id: string): Voucher | null {
        return this.voucherWithId(id)
    }

    private consumableWithId(id: string): Consumable | null {
        return this.consumables.find(c => c.id === id) || null
    }

    private menuWithId(id: string): Menu | null {
        return this.menus.find(m => m.id === id) || null
    }

    private voucherWithId(id: string): Voucher | null {
        return this.vouchers.find(v => v.id === id) || null
    }

    resolveProductName(skuId: string, menu: Menu | null): string | null {
        const menuItem = menu?.findMenuItemBySkuId(skuId) ?? null
        if (menuItem) {
            const consumable = this.consumables.find(c => c.id === menuItem.consumableId) || null
            return consumable?.displayName ?? null
        }
        const consumable = this.consumables.find(c => !!c.getSKUWithId(skuId)) || null
        return consumable?.displayName ?? null
    }

    resolveModifierName(skuId: string): string | null {
        for (const group of this.modifierGroups) {
            for (const mod of group.modifiers) {
                if (mod.skus.some(s => s.id === skuId)) {
                    return mod.displayName
                }
            }
        }
        return null
    }
}
